'use client'

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { HeartHandshake, Plus } from 'lucide-react'
import { PlayExperienceLoadingState } from '@/components/play/PlayExperienceLoadingState'
import { PlayIntelligenceHero } from '@/components/play/PlayIntelligenceHero'
import { PlayCircleOrbit } from '@/components/play/PlayCircleOrbit'
import { WagamourV2Button } from '@/components/v2/WagamourV2Button'
import { HomeV2GlassCard } from '@/components/home/HomeV2GlassCard'
import { ConnectOurDogsShareSheet } from '@/components/play/ConnectOurDogsShareSheet'
import { InviteDogParentShareSheet } from '@/components/play/InviteDogParentShareSheet'
import { IncomingConnectionReviewSheet } from '@/components/play/IncomingConnectionReviewSheet'
import { PlaydateBuilderPreviewSheet } from '@/components/play/PlaydateBuilderPreviewSheet'
import { PlayRelationshipSheet } from '@/components/play/PlayRelationshipSheet'
import { PlayIntegratedUpdatesSheet } from '@/components/play/PlayIntegratedUpdatesSheet'
import { CirclePlaydateProposalReviewSheet } from '@/components/play/CirclePlaydateProposalReviewSheet'
import { DevPreviewPlayRelationshipPreview } from '@/components/dev-preview/DevPreviewPlayRelationshipPreview'
import { DevPreviewPlayIntegratedUpdatesSheet } from '@/components/dev-preview/play-integrated/DevPreviewPlayIntegratedUpdatesSheet'
import { buildPlayRelationshipContentFromDto } from '@/lib/play/playRelationshipContent'
import {
  usePlayCirclePlaydatesEnabled,
  usePlayNotificationsMarkReadEnabled,
} from '@/lib/play/playCircleFlagContext'
import { markPlayUpdatesRead } from '@/lib/play/markPlayUpdatesRead'
import type { CirclePlaydateDto } from '@/lib/play/circlePlaydateDto'
import { formatCirclePlaydateWhenLabel } from '@/lib/play/formatCirclePlaydateWhen'
import { resolvePlayUpdateNavigation } from '@/lib/play/playUpdateNavigation'
import {
  loadCircleUpcomingPlaydates,
  reconcileUpcomingCirclePlaydates,
  removeCompletedCirclePlaydateFromItems,
  resolveSelectedOpenCirclePlaydate,
  selectEarliestFutureAgreedCirclePlaydate,
  upsertAgreedCirclePlaydateInItems,
} from '@/lib/play/circleUpcomingSource'
import { useCircleUpcoming } from '@/lib/play/circleUpcomingContext'
import type { PlayUpdateListItem } from '@/components/play/PlayIntegratedUpdatesSheet'

const ORBIT_HINT_STORAGE_KEY = 'wagamour.play.orbit-hint-dismissed'
import {
  mapCircleRelationshipsToOrbitFriends,
  circleCountToOrbitDensity,
  type PlayCircleOrbitFriend,
} from '@/lib/play/playCircleOrbitMapper'
import {
  mapPlayHeroStateToHeroContent,
  heroPrimaryCtaLabel,
  resolvePlayHomeGrowthCtaLayout,
  PLAY_HOME_CONNECT_GROWTH_LABEL,
  playCircleGrowthSupportCopy,
} from '@/lib/play/playHeroContentMapper'
import type { PlayIntegratedCoordinationPreview } from '@/lib/dev-preview-play/integrated/coordinationFixture'
import type { PlayIntegratedTransport } from '@/lib/play/playIntegratedTypes'
import type {
  PlayIntegratedCirclePayload,
  PlayIntegratedPendingPayload,
  PlayIntegratedTabSummaryPayload,
} from '@/lib/play/playIntegratedTypes'
import { homeV2Text, homeV2Type } from '@/lib/home/homeV2Theme'
import { playV2FocusRing } from '@/lib/play/playV2Focus'
import { playV2Type } from '@/lib/play/playV2Visual'
import {
  normalizePlayParkHandoff,
  resolveParkHandoffOpenMode,
  type NearbyPlaydateParkInput,
  type PlayParkHandoffSeed,
} from '@/lib/play/playParkHandoff'
import { trackPlayV2Event } from '@/lib/play/playV2Analytics'
import { resolvePlayLoadUserError } from '@/lib/play/playLoadErrorMessage'
import {
  markPlayStartup,
  resetPlayStartupTiming,
} from '@/lib/play/playStartupTiming'
import type { ParkContext } from '@/types/parks'

export type PlayIntegratedExperienceProps = {
  transport: PlayIntegratedTransport
  activeDogId: string
  shareSheetPortalContainer?: HTMLElement | null
  reloadToken?: number
  /** Navigate to Profile when Play Ready requires completion (same contract as PlayDashboard). */
  onNavigateToProfile?: (tab?: 'user' | 'dogs' | 'play') => void
  /** Parent refresh hook after persisted Play actions (e.g. Home upcoming summary). */
  onPlayActivity?: () => void
  /** Home → Play deep link: open relationship for this friend dog id. */
  initialFriendDogId?: string | null
  /** Home → Play deep link: open relationship by PlayRelationship id. */
  initialRelationshipId?: string | null
  /** Home → Play deep link: pin the specific playdate opened from Upcoming. */
  initialPlaydateId?: string | null
  onClearPlayDeepLink?: () => void
  /** Parks → Play one-time park intent (safe snapshot). */
  initialParkContext?: ParkContext | null
  /** Clear parent park context after successful one-time consume. */
  onParkContextConsumed?: () => void
  /**
   * Nearby parks for the app’s canonical selected location.
   * Used by the playdate builder — must match Home / Parks / Picks.
   */
  nearbyParks?: readonly NearbyPlaydateParkInput[] | null
  /** True while location or nearby parks are still hydrating. */
  nearbyParksLoading?: boolean
  /** Dev-only: show QR diagnostics and preview recipient action on Connect sheet. */
  showConnectPreviewExtras?: boolean
  /** Dev-only: show invite landing preview action. */
  showInvitePreviewLanding?: boolean
}

export function PlayIntegratedExperience({
  transport,
  activeDogId,
  shareSheetPortalContainer = null,
  reloadToken = 0,
  onNavigateToProfile,
  onPlayActivity,
  initialFriendDogId = null,
  initialRelationshipId = null,
  initialPlaydateId = null,
  onClearPlayDeepLink,
  initialParkContext = null,
  onParkContextConsumed,
  nearbyParks = null,
  nearbyParksLoading = false,
  showConnectPreviewExtras = false,
  showInvitePreviewLanding = false,
}: PlayIntegratedExperienceProps) {
  const connectCtaRef = useRef<HTMLDivElement>(null)
  const inviteCtaRef = useRef<HTMLButtonElement>(null)
  const orbitSectionRef = useRef<HTMLDivElement>(null)
  const parkHandoffConsumedKeysRef = useRef<Set<string>>(new Set())
  const viewTrackedRef = useRef(false)
  const firstContentMarkedRef = useRef(false)
  const [parkHandoffSeed, setParkHandoffSeed] =
    useState<PlayParkHandoffSeed | null>(null)
  const [summary, setSummary] =
    useState<PlayIntegratedTabSummaryPayload | null>(null)
  const [circle, setCircle] = useState<PlayIntegratedCirclePayload | null>(null)
  const [pending, setPending] = useState<PlayIntegratedPendingPayload | null>(
    null
  )
  const [coordinationOverlay, setCoordinationOverlay] =
    useState<PlayIntegratedCoordinationPreview | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [updatesOpen, setUpdatesOpen] = useState(false)
  const [shareSheetOpen, setShareSheetOpen] = useState(false)
  const [inviteSheetOpen, setInviteSheetOpen] = useState(false)
  const [incomingReviewOpen, setIncomingReviewOpen] = useState(false)
  const [playdateBuilderOpen, setPlaydateBuilderOpen] = useState(false)
  const [playdateReviewOpen, setPlaydateReviewOpen] = useState(false)
  const sharedUpcoming = useCircleUpcoming()
  const [localOpenPlaydateItems, setLocalOpenPlaydateItems] = useState<
    CirclePlaydateDto[]
  >([])
  const usingSharedUpcoming = Boolean(sharedUpcoming?.enabled)
  const openPlaydateItems = usingSharedUpcoming
    ? sharedUpcoming!.items
    : localOpenPlaydateItems
  const [selectedFriend, setSelectedFriend] =
    useState<PlayCircleOrbitFriend | null>(null)
  /** Explicit playdate from Updates/card/deep-link — never silently prefer another. */
  const [focusedPlaydateId, setFocusedPlaydateId] = useState<string | null>(
    null
  )
  /** One-shot Home deep link; never re-apply after Updates/card selection. */
  const homeDeepLinkConsumedKeyRef = useRef<string | null>(null)
  const suppressHomeDeepLinkRef = useRef(false)
  const [actionBusy, setActionBusy] = useState(false)
  const playdatesEnabled = usePlayCirclePlaydatesEnabled()
  const markReadEnabled = usePlayNotificationsMarkReadEnabled()
  const markedReadIdsRef = useRef<Set<string>>(new Set())

  const loadData = useCallback(async () => {
    setError(null)
    resetPlayStartupTiming()
    firstContentMarkedRef.current = false
    // loadData only runs after session + active-dog gate; mark that first.
    markPlayStartup('session_dog_gate_passed')
    markPlayStartup('load_start')
    try {
      const result = await transport.loadData(activeDogId, {
        onCoreReady: ({
          summary: coreSummary,
          circle: coreCircle,
          coordinationOverlay: coreCoord,
        }) => {
          markPlayStartup('core_ready')
          setSummary(coreSummary)
          setCircle(coreCircle)
          setCoordinationOverlay(coreCoord ?? null)
          markPlayStartup('state_applied_core')
        },
      })
      markPlayStartup('full_ready')
      setSummary(result.summary)
      setCircle(result.circle)
      setPending(result.pending)
      setCoordinationOverlay(result.coordinationOverlay ?? null)
      markPlayStartup('state_applied')
    } catch (err) {
      setError(resolvePlayLoadUserError(err, transport.mode))
    }
  }, [activeDogId, transport])

  useEffect(() => {
    loadData()
  }, [loadData, reloadToken])

  // Dog switch must not keep another dog's explicit playdate focus.
  useEffect(() => {
    setFocusedPlaydateId(null)
    suppressHomeDeepLinkRef.current = false
    homeDeepLinkConsumedKeyRef.current = null
  }, [activeDogId])

  useEffect(() => {
    if (summary?.hero && circle && !firstContentMarkedRef.current) {
      firstContentMarkedRef.current = true
      markPlayStartup('first_meaningful_content')
    }
  }, [summary?.hero, circle])
  const loadOpenPlaydates = useCallback(
    async (opts?: { retain?: CirclePlaydateDto | null }) => {
      if (!playdatesEnabled || transport.mode === 'preview') {
        if (!usingSharedUpcoming) setLocalOpenPlaydateItems([])
        return
      }
      // Production app-beta: one shared Circle upcoming source with Home.
      if (sharedUpcoming?.enabled) {
        await sharedUpcoming.refresh(opts)
        return
      }
      try {
        const result = await loadCircleUpcomingPlaydates(activeDogId)
        if (!result.ok || !result.parsedOk) return
        setLocalOpenPlaydateItems((previous) =>
          reconcileUpcomingCirclePlaydates({
            fetched: result.items,
            previous,
            completedPlaydateIds: new Set(),
            retain: opts?.retain,
            activeDogId,
          })
        )
      } catch {
        // Keep Circle UX available even if playdate list fails.
      }
    },
    [
      activeDogId,
      playdatesEnabled,
      sharedUpcoming,
      transport.mode,
      usingSharedUpcoming,
    ]
  )

  useEffect(() => {
    if (usingSharedUpcoming) return
    void loadOpenPlaydates()
  }, [loadOpenPlaydates, reloadToken, usingSharedUpcoming])

  const hero = summary?.hero
  const heroContent = useMemo(
    () => (hero ? mapPlayHeroStateToHeroContent(hero) : null),
    [hero]
  )

  const inboundPending = useMemo(() => {
    const inbound = pending?.pending?.inbound ?? []
    const ref = hero?.pendingInbound?.connectionRef
    if (!ref || inbound.length === 0) return null
    return (
      inbound.find((item) => item.connectionRef === ref) ?? inbound[0] ?? null
    )
  }, [hero?.pendingInbound?.connectionRef, pending?.pending?.inbound])

  const runPendingAction = useCallback(
    async (
      connectionRef: string,
      action: 'accept' | 'decline',
      recipientDogId: string
    ) => {
      setActionBusy(true)
      try {
        const result = await transport.runPendingAction(
          connectionRef,
          action,
          recipientDogId
        )
        if (!result.ok) {
          return { ok: false as const, message: result.message }
        }
        await loadData()
        onPlayActivity?.()
        return { ok: true as const }
      } finally {
        setActionBusy(false)
      }
    },
    [loadData, onPlayActivity, transport]
  )

  const handleAcceptInbound = useCallback(
    async (connectionRef: string, recipientDogId: string) => {
      const friendName = inboundPending?.initiatorDog?.name ?? 'Friend'
      const result = await runPendingAction(
        connectionRef,
        'accept',
        recipientDogId
      )
      if (!result.ok) return result
      return { ok: true as const, friendName }
    },
    [inboundPending?.initiatorDog?.name, runPendingAction]
  )

  const handleDeclineInbound = useCallback(
    async (connectionRef: string, recipientDogId: string) =>
      runPendingAction(connectionRef, 'decline', recipientDogId),
    [runPendingAction]
  )

  const [completeBusy, setCompleteBusy] = useState(false)
  const [cancelBusy, setCancelBusy] = useState(false)
  const [orbitHintVisible, setOrbitHintVisible] = useState(false)

  const orbitFriends = useMemo(
    () =>
      mapCircleRelationshipsToOrbitFriends(circle?.circle?.relationships ?? []),
    [circle?.circle?.relationships]
  )
  const orbitState = circleCountToOrbitDensity(circle?.circle?.totalCount ?? 0)

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (orbitFriends.length === 0) {
      setOrbitHintVisible(false)
      return
    }
    setOrbitHintVisible(
      window.sessionStorage.getItem(ORBIT_HINT_STORAGE_KEY) !== '1'
    )
  }, [orbitFriends.length])

  // Integrated Play view — once per mount / active dog.
  useEffect(() => {
    if (viewTrackedRef.current) return
    viewTrackedRef.current = true
    trackPlayV2Event(
      'play_v2_viewed',
      { activeDogId, source: 'play_tab' },
      { dedupeKey: `play_v2_viewed_${activeDogId}` }
    )
    trackPlayV2Event(
      'play_circle_viewed',
      { activeDogId, source: 'play_tab', friendCount: orbitFriends.length },
      { dedupeKey: `play_circle_viewed_${activeDogId}` }
    )
  }, [activeDogId, orbitFriends.length])

  // Parks → Play one-time handoff: copy into local seed, clear parent, maybe auto-open builder.
  useEffect(() => {
    if (!circle?.circle) return
    const seed = normalizePlayParkHandoff(initialParkContext)
    if (!seed) return
    if (parkHandoffConsumedKeysRef.current.has(seed.consumeKey)) return
    parkHandoffConsumedKeysRef.current.add(seed.consumeKey)
    setParkHandoffSeed(seed)
    onParkContextConsumed?.()
    trackPlayV2Event(
      'parks_playdate_handoff_consumed',
      {
        activeDogId,
        parkId: seed.parkId,
        source: 'parks',
        friendCount: orbitFriends.length,
      },
      { dedupeKey: `parks_playdate_handoff_consumed_${seed.consumeKey}` }
    )

    const mode = resolveParkHandoffOpenMode(orbitFriends.length)
    if (mode === 'auto_builder' && playdatesEnabled && orbitFriends[0]) {
      setFocusedPlaydateId(null)
      suppressHomeDeepLinkRef.current = false
      setSelectedFriend(orbitFriends[0])
      setRelationshipOutcome(null)
      setPlaydateBuilderOpen(true)
    }
  }, [
    circle?.circle,
    initialParkContext,
    onParkContextConsumed,
    activeDogId,
    orbitFriends,
    playdatesEnabled,
  ])

  const dismissOrbitHint = useCallback(() => {
    setOrbitHintVisible(false)
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(ORBIT_HINT_STORAGE_KEY, '1')
    }
  }, [])

  const createShareToken = useCallback(
    () => transport.createShareToken(activeDogId),
    [activeDogId, transport]
  )

  const clearPlaydateFocus = useCallback(() => {
    setFocusedPlaydateId(null)
    suppressHomeDeepLinkRef.current = false
  }, [])

  const openFriendByRelationshipId = useCallback(
    (
      relationshipId: string,
      playdateId?: string | null,
      opts?: { suppressHomeDeepLink?: boolean }
    ) => {
      const relationships = circle?.circle?.relationships ?? []
      const rel = relationships.find((r) => r.relationshipId === relationshipId)
      if (!rel) return null
      const friend =
        orbitFriends.find((f) => f.id === rel.friendDog.id) ??
        ({
          id: rel.friendDog.id,
          name: rel.friendDog.name,
          photoUrl: rel.friendDog.photoUrl,
        } satisfies PlayCircleOrbitFriend)
      if (opts?.suppressHomeDeepLink) {
        suppressHomeDeepLinkRef.current = true
      }
      setFocusedPlaydateId(playdateId?.trim() ? playdateId.trim() : null)
      setSelectedFriend(friend)
      return { friend, relationship: rel }
    },
    [circle?.circle?.relationships, orbitFriends]
  )

  // Home Upcoming → open relationship once. Never overwrite Updates/card focus.
  useEffect(() => {
    if (!circle?.circle?.relationships) return
    if (!initialRelationshipId && !initialFriendDogId) return

    const deepLinkKey = [
      initialRelationshipId ?? '',
      initialFriendDogId ?? '',
      initialPlaydateId ?? '',
    ].join('|')

    if (
      suppressHomeDeepLinkRef.current ||
      homeDeepLinkConsumedKeyRef.current === deepLinkKey
    ) {
      onClearPlayDeepLink?.()
      return
    }

    if (initialRelationshipId) {
      const opened = openFriendByRelationshipId(
        initialRelationshipId,
        initialPlaydateId
      )
      if (opened) {
        homeDeepLinkConsumedKeyRef.current = deepLinkKey
        setRelationshipOutcome(null)
        trackPlayV2Event(
          'home_playdate_opened',
          {
            activeDogId,
            relationshipId: initialRelationshipId,
            playdateId: initialPlaydateId ?? null,
            source: 'home',
          },
          { dedupeKey: `home_playdate_opened_${initialRelationshipId}` }
        )
        trackPlayV2Event('relationship_opened', {
          activeDogId,
          relationshipId: initialRelationshipId,
          source: 'home',
        })
        onClearPlayDeepLink?.()
      }
      return
    }

    if (!initialFriendDogId) return
    const rel = circle.circle.relationships.find(
      (r) => r.friendDog.id === initialFriendDogId
    )
    if (!rel) return
    const opened = openFriendByRelationshipId(
      rel.relationshipId,
      initialPlaydateId
    )
    if (opened) {
      homeDeepLinkConsumedKeyRef.current = deepLinkKey
      setRelationshipOutcome(null)
      trackPlayV2Event(
        'home_playdate_opened',
        {
          activeDogId,
          relationshipId: rel.relationshipId,
          playdateId: initialPlaydateId ?? null,
          source: 'home',
        },
        { dedupeKey: `home_playdate_opened_${rel.relationshipId}` }
      )
      trackPlayV2Event('relationship_opened', {
        activeDogId,
        relationshipId: rel.relationshipId,
        source: 'home',
      })
      onClearPlayDeepLink?.()
    }
  }, [
    circle?.circle?.relationships,
    initialFriendDogId,
    initialPlaydateId,
    initialRelationshipId,
    onClearPlayDeepLink,
    openFriendByRelationshipId,
    activeDogId,
  ])

  const handleSelectUpdate = useCallback(
    (item: PlayUpdateListItem) => {
      // Fire-and-forget mark-read — never blocks invitation / relationship navigation.
      if (
        markReadEnabled &&
        transport.mode !== 'preview' &&
        item.readAt == null &&
        !markedReadIdsRef.current.has(item.id)
      ) {
        markedReadIdsRef.current.add(item.id)
        setSummary((prev) => {
          if (!prev?.updates) return prev
          const nextCount = Math.max(0, (prev.updates.count ?? 0) - 1)
          return {
            ...prev,
            updates: {
              ...prev.updates,
              count: nextCount,
              visible: nextCount > 0,
            },
          }
        })
        void markPlayUpdatesRead([item.id])
      }

      const nav = resolvePlayUpdateNavigation(item)
      setUpdatesOpen(false)
      // Never carry a prior complete/cancel banner into a fresh Updates navigation.
      setRelationshipOutcome(null)
      if (nav.kind === 'none') return

      const relationshipId =
        nav.relationshipId ??
        (nav.playdateId
          ? openPlaydateItems.find((p) => p.id === nav.playdateId)
              ?.relationshipId
          : null)
      if (!relationshipId) return

      const opened = openFriendByRelationshipId(relationshipId, nav.playdateId, {
        suppressHomeDeepLink: true,
      })
      if (!opened) return

      const openPd = resolveSelectedOpenCirclePlaydate({
        items: openPlaydateItems,
        relationshipId,
        focusedPlaydateId: nav.playdateId,
      })
      const isInviteeProposed =
        openPd?.status === 'proposed' && openPd.viewerRole === 'invitee'

      if (nav.kind === 'review_playdate' && isInviteeProposed) {
        setPlaydateReviewOpen(true)
        trackPlayV2Event('playdate_invitation_opened', {
          activeDogId,
          relationshipId,
          playdateId: openPd?.id ?? null,
          source: 'updates',
        })
        return
      }

      if (
        (item.type === 'playdate_proposed' ||
          nav.kind === 'open_relationship') &&
        isInviteeProposed
      ) {
        setPlaydateReviewOpen(true)
        trackPlayV2Event('playdate_invitation_opened', {
          activeDogId,
          relationshipId,
          playdateId: openPd?.id ?? null,
          source: 'updates',
        })
      }
    },
    [
      activeDogId,
      markReadEnabled,
      openFriendByRelationshipId,
      openPlaydateItems,
      transport.mode,
    ]
  )

  const handleConnect = () => {
    if (!hero || actionBusy) return
    if (hero.primaryCta.action === 'REVIEW_CONNECTION') {
      setIncomingReviewOpen(true)
      return
    }
    if (hero.primaryCta.action === 'COMPLETE_PROFILE') {
      onNavigateToProfile?.(hero.playReady?.profileTab ?? 'dogs')
      return
    }
    if (hero.primaryCta.action === 'VIEW_CIRCLE') {
      trackPlayV2Event('view_circle_opened', {
        activeDogId,
        source: 'play_tab',
        friendCount: orbitFriends.length,
      })
      orbitSectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
      if (orbitFriends.length === 1) {
        clearPlaydateFocus()
        setSelectedFriend(orbitFriends[0]!)
        dismissOrbitHint()
        trackPlayV2Event('relationship_opened', {
          activeDogId,
          source: 'orbit',
        })
      }
      return
    }
    if (hero.primaryCta.action === 'CONNECT') {
      trackPlayV2Event('connect_started', {
        activeDogId,
        source: 'connect',
      })
      setShareSheetOpen(true)
      return
    }
    trackPlayV2Event('connect_started', {
      activeDogId,
      source: 'connect',
    })
    setShareSheetOpen(true)
  }

  const openConnectShareSheet = (source: 'connect' | 'orbit') => {
    if (!hero || actionBusy) return
    if (!hero.playReady?.ready) return
    trackPlayV2Event('connect_started', {
      activeDogId,
      source,
    })
    setShareSheetOpen(true)
  }

  const handleInviteDogParent = () => {
    if (!hero?.secondaryCta?.enabled || actionBusy) return
    setInviteSheetOpen(true)
  }

  const isPreviewTransport = transport.mode === 'preview'

  const selectedRelationship = useMemo(() => {
    const relationships = circle?.circle?.relationships ?? []
    if (!selectedFriend || relationships.length === 0) return null
    return (
      relationships.find((rel) => rel.friendDog.id === selectedFriend.id) ??
      null
    )
  }, [circle?.circle?.relationships, selectedFriend])

  const selectedOpenPlaydate = useMemo(() => {
    if (!selectedRelationship) return null
    return resolveSelectedOpenCirclePlaydate({
      items: openPlaydateItems,
      relationshipId: selectedRelationship.relationshipId,
      focusedPlaydateId,
    })
  }, [focusedPlaydateId, openPlaydateItems, selectedRelationship])

  /** Explicit id set but not in open items — never fall back to preferred/future. */
  const explicitPlaydateMissing = Boolean(
    focusedPlaydateId && selectedRelationship && !selectedOpenPlaydate
  )

  const [relationshipOutcome, setRelationshipOutcome] = useState<string | null>(
    null
  )

  const clearOpenPlaydateLocally = useCallback(
    (relationshipId: string, playdateId?: string) => {
      if (sharedUpcoming?.enabled) {
        if (playdateId) {
          sharedUpcoming.applyOptimisticCompleted({
            playdateId,
            relationshipId,
          })
        } else {
          void sharedUpcoming.refresh()
        }
        return
      }
      if (playdateId) {
        setLocalOpenPlaydateItems((prev) =>
          removeCompletedCirclePlaydateFromItems(prev, playdateId, relationshipId)
        )
        return
      }
      setLocalOpenPlaydateItems((prev) =>
        prev.filter((item) => item.relationshipId !== relationshipId)
      )
    },
    [sharedUpcoming]
  )

  const upsertOpenPlaydateLocally = useCallback(
    (playdate: CirclePlaydateDto) => {
      if (sharedUpcoming?.enabled) {
        sharedUpcoming.applyOptimisticAgreed(playdate)
        return
      }
      setLocalOpenPlaydateItems((prev) =>
        upsertAgreedCirclePlaydateInItems(prev, playdate)
      )
    },
    [sharedUpcoming]
  )

  const handleCompletePlaydate = useCallback(async () => {
    if (
      !playdatesEnabled ||
      transport.mode === 'preview' ||
      !selectedOpenPlaydate ||
      selectedOpenPlaydate.status !== 'agreed' ||
      explicitPlaydateMissing ||
      completeBusy ||
      cancelBusy
    ) {
      return {
        ok: false as const,
        message: 'Playdate is not ready to complete.',
      }
    }
    setCompleteBusy(true)
    trackPlayV2Event('playdate_complete_tapped', {
      activeDogId,
      relationshipId: selectedOpenPlaydate.relationshipId,
      playdateId: selectedOpenPlaydate.id,
      source: 'relationship',
    })
    try {
      const res = await fetch(
        `/api/play/playdates/${encodeURIComponent(selectedOpenPlaydate.id)}/complete`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ dogId: activeDogId }),
        }
      )
      const json = (await res.json().catch(() => ({}))) as {
        success?: boolean
        error?: string
        playdate?: CirclePlaydateDto
      }
      if (!res.ok || !json.success) {
        return {
          ok: false as const,
          message: json.error ?? 'Could not complete playdate. Try again.',
        }
      }

      clearOpenPlaydateLocally(
        selectedOpenPlaydate.relationshipId,
        selectedOpenPlaydate.id
      )
      setRelationshipOutcome('Playdate completed — your friendship grew.')
      onPlayActivity?.()
      void loadData()
      void loadOpenPlaydates()
      return { ok: true as const }
    } catch {
      return { ok: false as const, message: 'Network error — try again.' }
    } finally {
      setCompleteBusy(false)
    }
  }, [
    activeDogId,
    cancelBusy,
    clearOpenPlaydateLocally,
    completeBusy,
    explicitPlaydateMissing,
    loadData,
    loadOpenPlaydates,
    onPlayActivity,
    playdatesEnabled,
    selectedOpenPlaydate,
    transport.mode,
  ])

  const handleCancelPlaydate = useCallback(async () => {
    if (
      !playdatesEnabled ||
      transport.mode === 'preview' ||
      !selectedOpenPlaydate ||
      selectedOpenPlaydate.status !== 'agreed' ||
      explicitPlaydateMissing ||
      new Date(selectedOpenPlaydate.scheduledStartAt).getTime() <= Date.now() ||
      cancelBusy ||
      completeBusy
    ) {
      return { ok: false as const, message: 'Playdate is not ready to cancel.' }
    }
    setCancelBusy(true)
    trackPlayV2Event('playdate_cancel_tapped', {
      activeDogId,
      relationshipId: selectedOpenPlaydate.relationshipId,
      playdateId: selectedOpenPlaydate.id,
      source: 'relationship',
    })
    try {
      const res = await fetch(
        `/api/play/playdates/${encodeURIComponent(selectedOpenPlaydate.id)}/cancel`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ dogId: activeDogId }),
        }
      )
      const json = (await res.json().catch(() => ({}))) as {
        success?: boolean
        error?: string
        playdate?: CirclePlaydateDto
      }
      if (!res.ok || !json.success) {
        return {
          ok: false as const,
          message: json.error ?? 'Could not cancel playdate. Try again.',
        }
      }

      clearOpenPlaydateLocally(
        selectedOpenPlaydate.relationshipId,
        selectedOpenPlaydate.id
      )
      setRelationshipOutcome('Playdate cancelled — you stay friends.')
      onPlayActivity?.()
      void loadData()
      void loadOpenPlaydates()
      return { ok: true as const }
    } catch {
      return { ok: false as const, message: 'Network error — try again.' }
    } finally {
      setCancelBusy(false)
    }
  }, [
    activeDogId,
    cancelBusy,
    clearOpenPlaydateLocally,
    completeBusy,
    explicitPlaydateMissing,
    loadData,
    loadOpenPlaydates,
    onPlayActivity,
    playdatesEnabled,
    selectedOpenPlaydate,
    transport.mode,
  ])

  const productionRelationshipContent = useMemo(() => {
    if (!selectedRelationship || !circle?.activeDog) return null
    const suppressCompleteAction =
      completeBusy ||
      explicitPlaydateMissing ||
      relationshipOutcome === 'Playdate completed — your friendship grew.'
    return buildPlayRelationshipContentFromDto({
      activeDogName: circle.activeDog.name,
      relationship: selectedRelationship,
      openPlaydate: playdatesEnabled ? selectedOpenPlaydate : null,
      playdatesAvailable: playdatesEnabled,
      suppressCompleteAction,
      explicitPlaydateMissing,
    })
  }, [
    circle?.activeDog,
    completeBusy,
    explicitPlaydateMissing,
    playdatesEnabled,
    relationshipOutcome,
    selectedOpenPlaydate,
    selectedRelationship,
  ])

  const livePlaydateStatusById = useMemo(() => {
    const map: Record<string, string> = {}
    for (const pd of openPlaydateItems) {
      map[pd.id] = pd.status
    }
    return map
  }, [openPlaydateItems])

  const agreedUpcoming = useMemo(() => {
    if (!playdatesEnabled) return null
    return (
      selectEarliestFutureAgreedCirclePlaydate(openPlaydateItems) ??
      openPlaydateItems
        .filter((p) => p.status === 'agreed')
        .sort(
          (a, b) =>
            new Date(a.scheduledStartAt).getTime() -
            new Date(b.scheduledStartAt).getTime()
        )[0] ??
      null
    )
  }, [openPlaydateItems, playdatesEnabled])

  const plannedFriendIds = useMemo(() => {
    const ids = new Set<string>()
    for (const pd of openPlaydateItems) {
      if (pd.status !== 'proposed' && pd.status !== 'agreed') continue
      const friendId =
        pd.initiatorDog.id === activeDogId
          ? pd.inviteeDog.id
          : pd.initiatorDog.id
      ids.add(friendId)
    }
    return ids
  }, [activeDogId, openPlaydateItems])

  if (error) {
    return <p className="text-sm text-amber-100">{error}</p>
  }

  if (!hero || !heroContent || !circle) {
    return <PlayExperienceLoadingState />
  }

  const updatesCount = summary?.updates?.count ?? 0
  const coordination = coordinationOverlay
  const activeDogName = circle.activeDog?.name ?? 'Your dog'
  const activeDogPhotoUrl = circle.activeDog?.photoUrl ?? null
  const growthCta = resolvePlayHomeGrowthCtaLayout({
    playReady: Boolean(hero.playReady?.ready),
    primaryAction: hero.primaryCta.action,
    secondaryEnabled: Boolean(hero.secondaryCta?.enabled),
    circleTotalCount: hero.circleTotalCount ?? orbitFriends.length,
  })

  return (
    <div
      className="flex flex-col gap-4"
      data-play-integrated-experience="true"
      data-play-integrated-transport={transport.mode}
    >
      <PlayIntelligenceHero
        content={heroContent}
        className="!px-4 !py-3.5 [&_h1]:text-[20px] [&_h1]:leading-tight"
        footer={
          <div className="mt-3 flex flex-col gap-2.5">
            {hero.playReady?.ready ? (
              <p className="text-xs" style={homeV2Type(homeV2Text.muted)}>
                Play Ready
              </p>
            ) : (
              <p className="text-xs" style={homeV2Type(homeV2Text.muted)}>
                {hero.playReady?.message ?? 'Complete profile details'}
              </p>
            )}
            <div
              ref={connectCtaRef}
              tabIndex={-1}
              className="w-full outline-none"
            >
              <WagamourV2Button
                type="button"
                fullWidth
                disabled={!hero.primaryCta?.enabled || actionBusy}
                onClick={handleConnect}
                data-integrated-share-cta={
                  hero.primaryCta?.action === 'CONNECT' ? 'true' : undefined
                }
                data-integrated-view-circle-cta={
                  hero.primaryCta?.action === 'VIEW_CIRCLE' ? 'true' : undefined
                }
                data-integrated-review-cta={
                  hero.primaryCta?.action === 'REVIEW_CONNECTION'
                    ? 'true'
                    : undefined
                }
              >
                <span className="inline-flex items-center justify-center gap-2">
                  <HeartHandshake className="h-4 w-4 shrink-0" aria-hidden />
                  {heroPrimaryCtaLabel(hero)}
                </span>
              </WagamourV2Button>
            </div>
            {growthCta.secondaryAction === 'CONNECT' ? (
              <WagamourV2Button
                type="button"
                fullWidth
                variant="secondary"
                disabled={actionBusy}
                onClick={() => openConnectShareSheet('connect')}
                data-integrated-share-cta="true"
                data-integrated-connect-growth-cta="true"
                aria-label="Connect our Dogs to grow the Play Circle"
              >
                <span className="inline-flex items-center justify-center gap-2">
                  <Plus className="h-4 w-4 shrink-0" aria-hidden />
                  {PLAY_HOME_CONNECT_GROWTH_LABEL.replace(/^\+\s*/, '')}
                </span>
              </WagamourV2Button>
            ) : null}
            {growthCta.secondaryAction === 'INVITE' ||
            growthCta.showInviteTertiary ? (
              <button
                ref={inviteCtaRef}
                type="button"
                className={`mx-auto block min-h-[44px] rounded-full bg-transparent px-2 text-sm font-medium underline [text-underline-offset:2px] ${playV2FocusRing}`}
                style={homeV2Type(homeV2Text.muted)}
                disabled={!hero.secondaryCta?.enabled || actionBusy}
                onClick={handleInviteDogParent}
                data-integrated-invite-cta="true"
              >
                Invite a Dog Parent
              </button>
            ) : null}
          </div>
        }
      />

      <div ref={orbitSectionRef} data-play-circle-section="true">
        <PlayCircleOrbit
          activeDog={{
            name: activeDogName,
            photoUrl: activeDogPhotoUrl,
          }}
          friends={orbitFriends}
          state={orbitState}
          layoutProfile="integrated"
          copyProfile="integrated"
          onboardingHint={
            orbitHintVisible && orbitFriends.length > 0
              ? playCircleGrowthSupportCopy(activeDogName)
              : null
          }
          plannedFriendIds={plannedFriendIds}
          onConnectDogs={
            growthCta.showCircleConnectAction
              ? () => openConnectShareSheet('orbit')
              : undefined
          }
          connectDogsDisabled={actionBusy}
          onFriendSelect={(friend) => {
            dismissOrbitHint()
            setRelationshipOutcome(null)
            clearPlaydateFocus()
            setSelectedFriend(friend)
            const rel = (circle?.circle?.relationships ?? []).find(
              (r) => r.friendDog.id === friend.id
            )
            trackPlayV2Event('relationship_opened', {
              activeDogId,
              relationshipId: rel?.relationshipId ?? null,
              source: 'orbit',
            })
          }}
        />
      </div>

      {updatesCount > 0 || playdatesEnabled ? (
        <button
          type="button"
          onClick={() => setUpdatesOpen(true)}
          className={`w-full text-left ${playV2FocusRing}`}
          data-play-updates-entry="true"
          aria-label={
            updatesCount > 0
              ? `Open updates, ${updatesCount} unread`
              : 'Open updates'
          }
        >
          <HomeV2GlassCard variant="surface" className="min-h-[44px] px-4 py-3">
            <p className="text-sm font-semibold" style={playV2Type.headline}>
              {updatesCount > 0 ? `Updates (${updatesCount})` : 'Updates'}
            </p>
            <p className="mt-0.5 text-[11px]" style={playV2Type.metadata}>
              {updatesCount > 0
                ? 'Invitations and relationship activity'
                : 'No new updates'}
            </p>
          </HomeV2GlassCard>
        </button>
      ) : null}

      {agreedUpcoming ? (
        <button
          type="button"
          className={`w-full text-left ${playV2FocusRing}`}
          data-playdate-upcoming-card="true"
          aria-label={`Open upcoming playdate at ${agreedUpcoming.parkName || agreedUpcoming.customLocationLabel || 'public place'}`}
          onClick={() => {
            const relId = agreedUpcoming.relationshipId
            const opened = openFriendByRelationshipId(
              relId,
              agreedUpcoming.id,
              { suppressHomeDeepLink: true }
            )
            if (!opened) return
            if (
              agreedUpcoming.status === 'proposed' &&
              agreedUpcoming.viewerRole === 'invitee'
            ) {
              setPlaydateReviewOpen(true)
            }
          }}
        >
          <HomeV2GlassCard variant="surface" className="px-4 py-3">
            <p
              className="text-[10px] font-semibold uppercase tracking-[0.14em]"
              style={playV2Type.eyebrow}
            >
              Upcoming playdate
            </p>
            <p
              className="mt-1 text-sm font-semibold"
              style={playV2Type.headline}
            >
              Confirmed ·{' '}
              {agreedUpcoming.parkName ||
                agreedUpcoming.customLocationLabel ||
                'Public place'}
            </p>
            <p className="mt-1 text-[11px]" style={playV2Type.metadata}>
              {formatCirclePlaydateWhenLabel({
                scheduledStartAt: agreedUpcoming.scheduledStartAt,
                timezone: agreedUpcoming.timezone,
                presentationMode: agreedUpcoming.presentationMode,
              })}
            </p>
          </HomeV2GlassCard>
        </button>
      ) : coordination?.visible ? (
        <HomeV2GlassCard variant="surface" className="px-4 py-3">
          <p
            className="text-[10px] font-semibold uppercase tracking-[0.14em]"
            style={playV2Type.eyebrow}
          >
            Coordination
          </p>
          <p className="mt-1 text-sm font-semibold" style={playV2Type.headline}>
            {coordination.scheduleLine}
          </p>
          {transport.mode === 'preview' ? (
            <p className="mt-1 text-[11px]" style={playV2Type.metadata}>
              Preview-only fixture — playdate persistence is Slice 3.
            </p>
          ) : null}
        </HomeV2GlassCard>
      ) : null}

      <ConnectOurDogsShareSheet
        isOpen={shareSheetOpen}
        onClose={() => setShareSheetOpen(false)}
        dog={{
          name: activeDogName,
          photoUrl: activeDogPhotoUrl,
        }}
        createShareToken={createShareToken}
        showPreviewRecipientAction={showConnectPreviewExtras}
        showQrDiagnostics={showConnectPreviewExtras}
        portalContainer={shareSheetPortalContainer}
        returnFocusRef={connectCtaRef}
      />

      <IncomingConnectionReviewSheet
        isOpen={incomingReviewOpen}
        onClose={() => setIncomingReviewOpen(false)}
        inbound={inboundPending}
        preferredRecipientDogId={activeDogId}
        recipientDogFallback={{
          name: activeDogName,
          photoUrl: activeDogPhotoUrl,
        }}
        onAccept={handleAcceptInbound}
        onDecline={handleDeclineInbound}
        onViewRelationship={(friendDogId) => {
          const friend = orbitFriends.find((f) => f.id === friendDogId)
          clearPlaydateFocus()
          if (friend) {
            setRelationshipOutcome(null)
            setSelectedFriend(friend)
          } else {
            // Circle may still be refreshing — keep friend identity for sheet open after reload
            setSelectedFriend({
              id: friendDogId,
              name: inboundPending?.initiatorDog?.name ?? 'Friend',
              photoUrl: inboundPending?.initiatorDog?.photoUrl ?? null,
            })
          }
          orbitSectionRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          })
        }}
        portalContainer={shareSheetPortalContainer}
        returnFocusRef={connectCtaRef}
      />

      <InviteDogParentShareSheet
        isOpen={inviteSheetOpen}
        onClose={() => setInviteSheetOpen(false)}
        showPreviewLandingAction={showInvitePreviewLanding}
        portalContainer={shareSheetPortalContainer}
        returnFocusRef={inviteCtaRef}
      />

      <DevPreviewPlayIntegratedUpdatesSheet
        isOpen={isPreviewTransport && updatesOpen}
        count={updatesCount}
        onClose={() => setUpdatesOpen(false)}
      />

      <PlayIntegratedUpdatesSheet
        isOpen={!isPreviewTransport && updatesOpen}
        count={updatesCount}
        onClose={() => setUpdatesOpen(false)}
        dogId={activeDogId}
        playdatesEnabled={playdatesEnabled}
        livePlaydateStatusById={livePlaydateStatusById}
        onSelectUpdate={handleSelectUpdate}
      />

      <DevPreviewPlayRelationshipPreview
        isOpen={
          isPreviewTransport && selectedFriend != null && !playdateBuilderOpen
        }
        activeDog={{ name: activeDogName, photoUrl: activeDogPhotoUrl }}
        friend={selectedFriend}
        onClose={() => setSelectedFriend(null)}
        onPlanPlaydate={() => setPlaydateBuilderOpen(true)}
        portalContainer={shareSheetPortalContainer}
      />

      <PlayRelationshipSheet
        isOpen={
          !isPreviewTransport &&
          selectedFriend != null &&
          !playdateBuilderOpen &&
          !playdateReviewOpen
        }
        activeDog={{ name: activeDogName, photoUrl: activeDogPhotoUrl }}
        friend={selectedFriend}
        content={productionRelationshipContent}
        outcomeBanner={relationshipOutcome}
        onClose={() => {
          setRelationshipOutcome(null)
          clearPlaydateFocus()
          setSelectedFriend(null)
        }}
        onPlanPlaydate={() => {
          if (!playdatesEnabled) return
          setPlaydateBuilderOpen(true)
        }}
        onReviewPlaydate={() => {
          setPlaydateReviewOpen(true)
          if (selectedOpenPlaydate) {
            trackPlayV2Event('playdate_invitation_opened', {
              activeDogId,
              relationshipId: selectedOpenPlaydate.relationshipId,
              playdateId: selectedOpenPlaydate.id,
              source: 'relationship',
            })
          }
        }}
        onCompletePlaydate={handleCompletePlaydate}
        onCancelPlaydate={handleCancelPlaydate}
        portalContainer={shareSheetPortalContainer}
      />

      <CirclePlaydateProposalReviewSheet
        isOpen={
          playdateReviewOpen && selectedOpenPlaydate?.status === 'proposed'
        }
        playdate={
          selectedOpenPlaydate?.status === 'proposed' &&
          selectedOpenPlaydate.viewerRole === 'invitee'
            ? selectedOpenPlaydate
            : null
        }
        activeDog={{ name: activeDogName, photoUrl: activeDogPhotoUrl }}
        onClose={() => setPlaydateReviewOpen(false)}
        onResolved={(playdate, outcome) => {
          setPlaydateReviewOpen(false)
          if (outcome === 'declined') {
            clearOpenPlaydateLocally(playdate.relationshipId, playdate.id)
            setRelationshipOutcome('Invitation declined — you stay friends.')
          } else {
            upsertOpenPlaydateLocally(playdate)
            setRelationshipOutcome("Playdate accepted — you're meeting up.")
          }
          openFriendByRelationshipId(playdate.relationshipId, playdate.id, {
            suppressHomeDeepLink: true,
          })
          onPlayActivity?.()
          void loadData()
          void loadOpenPlaydates({
            retain: outcome === 'accepted' ? playdate : null,
          })
        }}
        portalContainer={shareSheetPortalContainer}
      />

      {selectedFriend && (playdatesEnabled || isPreviewTransport) ? (
        <div
          data-playdate-capability={
            playdatesEnabled && !isPreviewTransport ? 'persist' : 'preview-only'
          }
        >
          <PlaydateBuilderPreviewSheet
            isOpen={playdateBuilderOpen}
            onClose={() => {
              setPlaydateBuilderOpen(false)
              // One-time handoff: drop local seed when the planning sheet closes
              // so refresh / re-entry does not reopen with the same park.
              setParkHandoffSeed(null)
            }}
            activeDog={{ name: activeDogName, photoUrl: activeDogPhotoUrl }}
            seedFriend={selectedFriend}
            seedPark={parkHandoffSeed}
            nearbyParks={nearbyParks}
            nearbyParksLoading={nearbyParksLoading}
            allowFixtureParks={isPreviewTransport}
            onSeedParkApplied={() => {
              // Parent context already cleared on consume; keep local seed for this sheet session.
            }}
            portalContainer={shareSheetPortalContainer}
            persistence={
              playdatesEnabled && !isPreviewTransport && selectedRelationship
                ? {
                    dogId: activeDogId,
                    relationshipId: selectedRelationship.relationshipId,
                    onProposed: () => {
                      onPlayActivity?.()
                      void loadOpenPlaydates()
                    },
                  }
                : null
            }
          />
        </div>
      ) : null}
    </div>
  )
}
